import { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../lib/axios';

export const ProductAttributeContext = createContext();

export const ProductAttributeProvider = ({ children }) => {
    const [attributes, setAttributes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAttributes = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log("Fetching product attributes...");
            const res = await API.get("/product-attributes");
            console.log("Product attributes response:", res.data);
            setAttributes(res.data || []);
        } catch (error) {
            console.error("Failed to load product attributes", error);
            setError("Failed to load attributes");
            // Set empty array as fallback
            setAttributes([]);
        } finally {
            setLoading(false);
        }
    };

    const getAttributesByMainCategory = (mainCategoryId) => {
        return attributes.filter(attr => attr.mainCategoryId === mainCategoryId);
    };

    const getAttributeByName = (name, mainCategoryId) => {
        return attributes.find(attr => 
            attr.name === name && attr.mainCategoryId === mainCategoryId
        );
    };

    const createAttribute = async (attributeData) => {
        try {
            const res = await API.post("/product-attributes", attributeData);
            setAttributes(prev => [...prev, res.data]);
            return res.data;
        } catch (error) {
            console.error("Failed to create attribute", error);
            throw error;
        }
    };

    const updateAttribute = async (id, attributeData) => {
        try {
            const res = await API.patch(`/product-attributes/${id}`, attributeData);
            setAttributes(prev => 
                prev.map(attr => attr.id === id ? res.data : attr)
            );
            return res.data;
        } catch (error) {
            console.error("Failed to update attribute", error);
            throw error;
        }
    };

    const deleteAttribute = async (id) => {
        try {
            await API.delete(`/product-attributes/${id}`);
            setAttributes(prev => prev.filter(attr => attr.id !== id));
        } catch (error) {
            console.error("Failed to delete attribute", error);
            throw error;
        }
    };

    // Defer attributes loading - only needed for admin/product creation
    // Not needed for viewing product pages
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAttributes();
        }, 500); // Defer by 500ms - attributes are only needed for admin forms

        return () => clearTimeout(timer);
    }, []);

    const value = {
        attributes,
        loading,
        error,
        fetchAttributes,
        getAttributesByMainCategory,
        getAttributeByName,
        createAttribute,
        updateAttribute,
        deleteAttribute
    };

    return (
        <ProductAttributeContext.Provider value={value}>
            {children}
        </ProductAttributeContext.Provider>
    );
};

export const useProductAttributes = () => {
    const context = useContext(ProductAttributeContext);
    if (!context) {
        throw new Error('useProductAttributes must be used within a ProductAttributeProvider');
    }
    return context;
};
